import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SpeedLoss } from "../../services/ShiftAnalysisService";
import Panel from "./Panel";

interface Props {
  losses: SpeedLoss[];
}

const isInternal = (rating: string) => rating.toLowerCase().startsWith("int");

/**
 * "Speed losses" — production lost while running below target speed, split into
 * internal (our own slow-running) vs external (starved / throttled by up- or
 * downstream). Backed by shift_speed_class_agg; renders nothing when empty.
 */
const SpeedLosses: React.FC<Props> = ({ losses }) => {
  const { t } = useTranslation();

  const { rows, totalLost, pacePct } = useMemo(() => {
    const totalLost = losses.reduce((s, l) => s + l.lost_production, 0);
    const totalGood = losses.reduce((s, l) => s + l.good_production, 0);
    const totalTheo = losses.reduce((s, l) => s + l.theoretical_prod, 0);
    const pacePct = totalTheo > 0 ? (totalGood / totalTheo) * 100 : 0;
    const rows = losses
      .filter((l) => l.lost_production > 0)
      .sort((a, b) => b.lost_production - a.lost_production);
    return { rows, totalLost, pacePct };
  }, [losses]);

  if (totalLost <= 0) return null;

  return (
    <Panel
      label={t("speedLosses", "Speed losses")}
      meta={t("lostToSlowRunning", "lost to slow running")}
    >
      <div className="flex flex-col gap-4">
        {/* Headline */}
        <div className="flex items-end justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <span
              className="text-3xl font-extrabold tabular-nums tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              {totalLost.toLocaleString()}
            </span>
            <span
              className="text-xs"
              style={{ color: "var(--text-secondary)" }}
            >
              {t("productsLostSpeed", "products lost to speed")}
            </span>
          </div>
          <div className="text-right">
            <div
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "var(--text-secondary)" }}
            >
              {t("pace", "Pace")}
            </div>
            <div
              className="text-lg font-bold tabular-nums"
              style={{
                color:
                  pacePct >= 75
                    ? "#22c55e"
                    : pacePct >= 45
                      ? "#eab308"
                      : "#ef4444",
              }}
            >
              {Math.round(pacePct)}%
            </div>
          </div>
        </div>

        {/* Int / Ext split */}
        <div className="flex flex-col gap-2.5">
          {rows.map((l) => {
            const internal = isInternal(l.rating);
            const color = internal ? "#ef4444" : "#f59e0b";
            const pct =
              totalLost > 0 ? (l.lost_production / totalLost) * 100 : 0;
            return (
              <div key={l.rating} className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between">
                  <span className="flex items-center gap-2 text-sm">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span style={{ color: "var(--text-primary)" }}>
                      {internal
                        ? t("internalSlow", "Internal (our speed)")
                        : t("externalSlow", "External (starved / throttled)")}
                    </span>
                  </span>
                  <span className="flex items-baseline gap-2 shrink-0">
                    <span
                      className="text-sm font-bold tabular-nums"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {l.lost_production.toLocaleString()}
                    </span>
                    <span
                      className="text-[11px] tabular-nums"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      ({Math.round(pct)}%)
                    </span>
                  </span>
                </div>
                <div
                  className="h-2 w-full rounded-full overflow-hidden"
                  style={{ backgroundColor: "var(--bg-secondary)" }}
                >
                  <div
                    className="h-full rounded-full transition-[width] duration-700 ease-out"
                    style={{
                      width: `${Math.max(pct, 2)}%`,
                      background: `linear-gradient(90deg, ${color}, ${color}bb)`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
};

export default SpeedLosses;
